// Navigation-source tracking for analytics.
//
// Records where the user came FROM before landing on a content page (event/news),
// so the view report can carry `source` + `sourceId`. The value is written on click
// (by NavSourceTracker) into sessionStorage — which survives both soft in-zone
// navigation and hard cross-zone `<a>` loads (all zones share one origin) — and read
// once on the destination page's tracker mount. When nothing was stashed (direct hit,
// external referrer, hard reload) we fall back to a coarse referrer-based guess.

export type NavSource =
  | 'home'
  | 'category'
  | 'event'
  | 'news'
  | 'related_event'
  | 'related_news'
  | 'recommendation'
  | 'search'
  | 'external'
  | 'direct';

export interface NavSourceIntent {
  source: NavSource;
  sourceId?: string;
}

const STORAGE_KEY = 'op_nav_source';
// A stashed intent is only trusted briefly after the click that set it — otherwise a
// stale value from an abandoned navigation would mislabel an unrelated later view.
const INTENT_TTL_MS = 30_000;

// The 8 shipped app locales (see CLAUDE.md). Used to strip a leading `/xx` path segment.
const LOCALES = new Set(['en', 'es', 'pt', 'zh', 'ja', 'ko', 'de', 'id']);

interface StoredIntent extends NavSourceIntent {
  ts: number;
}

/** Path segments with a leading locale prefix removed. */
export function stripLocaleSegments(pathname: string | null | undefined): string[] {
  if (!pathname) return [];
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && LOCALES.has(segments[0]!)) {
    segments.shift();
  }
  return segments;
}

/** Stash the origin of the navigation the user is about to make. Best-effort. */
export function setNavSource(source: NavSource, sourceId?: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const payload: StoredIntent = {
      source,
      ...(sourceId ? { sourceId } : {}),
      ts: Date.now(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // storage disabled / full — nav source is best-effort, never throw.
  }
}

function readAndClearStash(): NavSourceIntent | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw) as Partial<StoredIntent>;
    if (!parsed?.source || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > INTENT_TTL_MS) return null;
    return { source: parsed.source, ...(parsed.sourceId ? { sourceId: parsed.sourceId } : {}) };
  } catch {
    return null;
  }
}

function coarseSourceFromPath(pathname: string): NavSource {
  const segments = stripLocaleSegments(pathname);
  if (segments.length === 0) return 'home';
  const head = segments[0];
  if (head === 'event') return 'event';
  if (head === 'news') return 'news';
  if (head === 'search') return 'search';
  if (head === 'category' || head === 'categories') return 'category';
  // Root feed / other listing pages read as the main feed.
  return 'home';
}

function inferFromReferrer(): NavSourceIntent {
  if (typeof document === 'undefined') return { source: 'direct' };
  const referrer = document.referrer;
  if (!referrer) return { source: 'direct' };
  try {
    const url = new URL(referrer);
    if (url.host !== location.host) return { source: 'external' };
    return { source: coarseSourceFromPath(url.pathname) };
  } catch {
    return { source: 'direct' };
  }
}

/**
 * Read (and clear) the navigation source for the page that is now mounting.
 * Prefers the explicit click-time stash; falls back to a referrer-based guess.
 */
export function consumeNavSource(): NavSourceIntent {
  return readAndClearStash() ?? inferFromReferrer();
}
